/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';
import BreadcrumbStructuredData from '@apps/components/BreadcrumbStructuredData';
import NewsArticleStructuredData from '@apps/components/NewsArticleStructuredData';
import SEOLocaleList from '@apps/components/SEOLocaleList';
import SideAds from '@apps/components/SideAds';
import { BASE_URL } from '@apps/config';
import getAds from '@apps/config/API/ads.config';
import { getPostDetails } from '@apps/config/API/details.config';
import getJpjRunningNumber from '@apps/config/API/jpj-running-number.config';
import getNewsListing from '@apps/config/API/listing.config';
import { getNewCarById } from '@apps/config/API/new-car.config';
import NewsDetailConsign from '@apps/containers/NewsDetailConsign';
import NewsListingScreen from '@apps/screens/NewsListing';
import { localeConversions } from '@apps/utils/locale-conversion';
import { Breadcrumb } from '@stack/ui/components';
import { BreadCrumbOptions } from '@stack/ui/components/Breadcrumb/props';
import { NextPage, NextPageContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import useTranslation from 'next-translate/useTranslation';

import { NewsDetailScreen } from '../../screens/NewsDetail/index';

export const config = { amp: 'hybrid' };

/**
 * ===========================
 * MAIN
 * ===========================
 */
const Page: NextPage = (props) => {
  // ======================= HOOKS
  const { settings, listing, detail, jpjNumber, webView, ads } = props;
  const router = useRouter();
  const { lang, t } = useTranslation('common');
  const newLang = localeConversions(lang);
  const facebookClientId = process.env.NEXT_PUBLIC_FACEBOOK_CLIENT_ID as string;

  // ======================= STATE
  const carBrand = settings?.car_brand_featured;
  const fuel = settings.fuel_category;
  const pageSize = 40;
  const [data, setData] = useState<any>();
  const [loading, setloading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [cars, setCars] = useState();

  // ======================= API
  const mainCategory = router.query?.category ?? '';
  const subCategory = router.query?.slug?.[0];

  const fetchData = async (pageNumber: number) => {
    try {
      const category = router.query?.category ?? '';
      const subCat = router.query?.slug?.[0];

      setloading(true);
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth' as any,
        });
      }, 0);

      const res = await getNewsListing({
        pageSize,
        page: pageNumber,
        locale: newLang,
        postCategory: category as string,
        ...(subCat && category !== subCat ? { subCategory: subCat } : {}),
      });
      if (res.data.length < 1) {
        router.replace(webView ? '/webview/404' : '/404');
        return;
      }

      const newEvCar = await getNewCarById({ id: 4 });
      const newCars = newEvCar?.data?.find((car: any) => {
        return car.new_car_feature_id === 4;
      });

      setCars(newCars);
      setData(res);
      setloading(false);
    } catch (err) {
      console.log('err', err);
    }
  };

  useEffect(() => {
    const { page } = router.query;
    if (!isDetail) {
      setCurrentPage(parseInt(page as string) || 1);
      fetchData(parseInt(page as string) || 1);
    }
  }, [router?.asPath, router?.locale]);

  const news = data;
  // ======================= VARIABLES
  const currentPath = router?.query?.category;
  const currentCategory = settings?.post_category?.find((pc: any) => {
    return pc.post_category_slug === currentPath;
  });
  const isDetail = router?.query?.slug?.[1];
  const subCategoryName = currentCategory?.post_category_item?.find(
    (pci: any) => {
      return pci.post_category_item_slug === subCategory;
    }
  )?.post_category_item_name[newLang];

  let breadcrumbOpt: BreadCrumbOptions[] = [
    {
      title: t('news'),
      url: webView ? '/webview' : '/',
    },
    {
      title:
        currentCategory?.post_category_name[newLang] ?? (currentPath as string),
    },
  ];

  if (subCategory && mainCategory !== subCategory) {
    breadcrumbOpt = [
      {
        title: t('news'),
        url: webView ? '/webview' : '/',
      },
      {
        title:
          currentCategory?.post_category_name[newLang] ??
          (currentPath as string),
        url: webView ? `/webview/${currentPath}` : `/${currentPath}`,
      },
      {
        title: subCategoryName,
      },
    ];
  }
  if (isDetail) {
    const categoryPath = detail?.data?.category_name[newLang] ?? '';
    const categorySlug = detail?.data?.category_slug ?? '';
    const subCategoryPath = detail?.data?.category_item_name[newLang] ?? '';
    const subCategorySlug = detail?.data?.category_item_slug;

    breadcrumbOpt = [
      {
        title: t('news'),
        url: webView ? '/webview' : '/',
      },
      {
        title: categoryPath,
        url: webView ? `/webview/${categorySlug}` : `/${categorySlug}`,
      },
    ];

    if (subCategorySlug) {
      breadcrumbOpt.push({
        title: subCategoryPath,
        url: webView
          ? `/webview/${categorySlug}/${subCategoryPath}`
          : `/${categorySlug}/${subCategorySlug}`,
      });
    }
    breadcrumbOpt.push({
      title:
        detail?.data?.post_title?.[newLang] || detail?.data?.post_title['en'],
    });
  }

  // ======================= VIEWS

  // ======================= SEO
  const seoTitle = isDetail
    ? `${detail?.data?.post_title[newLang]} | Carz Automedia Malaysia`
    : `Malaysia ${currentCategory?.post_category_name[newLang]} News - Latest Models & Upcoming Launches | Carz Automedia Malaysia`;
  const seoDesc = detail?.data?.post_short_description[newLang];
  const seoImage = detail?.data?.post_image[newLang];
  const localePrefix = lang === 'en' ? '' : `/${lang}`;
  const currentUrl = `${BASE_URL}${localePrefix}${router?.asPath.toLowerCase()}`;
  let cleanedPath = currentUrl.split('?')[0].toLowerCase();

  // Handle old post url, to remove id from it
  if (isDetail) {
    const id = router.query?.slug?.[2];
    if (id) {
      const parts = currentUrl.split('?')[0].split('/');
      parts.pop();
      cleanedPath = parts.join('/');
    }
  }

  const availableLang = detail?.data?.post_available_language as string;
  const hasData = availableLang?.includes(newLang);
  const showBreadcrumb = (hasData && isDetail) || !isDetail;

  return (
    <>
      <Head>
        {seoImage ? (
          <>
            <meta name="twitter:title" content={seoTitle} />
            <meta name="twitter:description" content={seoDesc} />
            <meta name="twitter:image" content={seoImage} />
            <meta name="twitter:image:type" content="image/jpeg" />
            <meta name="twitter:url" content={cleanedPath} />
          </>
        ) : (
          <meta name="twitter:url" content={cleanedPath} />
        )}
      </Head>
      <NextSeo
        title={seoTitle}
        titleTemplate={seoTitle}
        description={seoDesc}
        canonical={cleanedPath}
        openGraph={{
          ...(seoImage
            ? {
                url: cleanedPath,
                images: [
                  {
                    url: seoImage,
                    alt: seoTitle,
                    type: 'image/jpeg',
                  },
                ],
                type: !isDetail ? 'website' : 'article',
                article: isDetail
                  ? {
                      publishedTime: `${detail?.data?.post_created}`,
                      authors: [
                        `${BASE_URL}/author/${detail?.data?.post_author_username[
                          'en'
                        ]?.toLowerCase()}`,
                      ],
                    }
                  : undefined,
              }
            : { url: currentUrl }),
        }}
        facebook={{
          appId: facebookClientId,
        }}
      />
      <SEOLocaleList />
      {isDetail && hasData ? (
        <>
          <NewsArticleStructuredData
            data={detail?.data}
            url={cleanedPath}
            locale={newLang}
          />
          <BreadcrumbStructuredData
            items={breadcrumbOpt}
            localePrefix={localePrefix}
          />
        </>
      ) : null}
      <div className="body-wrapper">
        <SideAds
          adsKeyLeft={'news_detail_vertical_left'}
          adsKeyRight={'news_detail_vertical_right'}
        />
        {showBreadcrumb ? <Breadcrumb options={breadcrumbOpt} /> : <></>}
        {isDetail ? (
          <NewsDetailScreen
            relatedNews={detail?.related_post}
            data={detail?.data}
            listing={listing?.data}
            brands={carBrand}
            fuels={fuel}
            jpjNumber={jpjNumber}
            webView={webView}
            ads={ads}
          />
        ) : (
          <>
            <h1 className="text-3xl">
              {subCategory
                ? subCategoryName
                : currentCategory?.post_category_name[newLang]}
            </h1>
            <NewsListingScreen
              categorySlug={currentPath}
              data={news?.data ?? []}
              currentPage={currentPage}
              totalCount={news?.meta?.total ?? 0}
              pageSize={pageSize}
              carBrand={carBrand}
              newEvCar={cars}
              webView={webView}
              loading={loading}
              href={`/${currentPath}`}
            />
          </>
        )}
      </div>
      {isDetail && hasData && (
        <NewsDetailConsign
          webView={webView}
          locationState={settings?.location_state}
        />
      )}
    </>
  );
};

Page.layout = 'customContainer';

export async function getServerSideProps(ctx: NextPageContext) {
  const hasDetailSlug = ctx.query?.slug && ctx.query?.slug?.length > 1;
  const locale = ctx?.locale as string;
  const is_preview = (ctx.query?.preview as string) ?? 'false';
  const newLocale = localeConversions(locale);
  if (hasDetailSlug) {
    const titleSlug = ctx.query?.slug?.[1] as string;
    const id = ctx.query?.slug?.[2] as string;

    // if (id) {
    //   const url = ctx.req?.url ?? '';
    //   const parts = url.split('/');
    //   parts.pop();
    //   const newUrl = parts.join('/');
    //   return {
    //     redirect: {
    //       permanent: true,
    //       destination: newUrl,
    //     },
    //   };
    // }

    const detailsData = await getPostDetails({
      slug: titleSlug.toLowerCase(),
      id,
      locale: newLocale,
      preview: is_preview,
    });
    if (!detailsData?.data) {
      return {
        notFound: true,
      };
    }

    // Check is the post have the available language
    const availableLang = detailsData?.data?.post_available_language as string;
    const hasData = availableLang?.includes(newLocale);
    if (!hasData) {
      return {
        redirect: {
          destination: newLocale === 'en' ? '/' : `/${locale}`,
        },
      };
    }

    const [listingData, jpjNumber, ads]: any = await Promise.allSettled([
      getNewsListing({
        pageSize: 8,
        page: 1,
        locale: newLocale,
        postCategory: detailsData?.data?.category_slug ?? '',
      }),
      getJpjRunningNumber({ featured: true }),
      getAds('sm_details'),
    ]);

    return {
      props: {
        listing: listingData?.value || [],
        detail: detailsData,
        jpjNumber: jpjNumber?.value || [],
        ads: ads?.value || [],
      },
    };
  }

  return { props: {} };
}

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default Page;
